import { useState, useEffect, useRef, KeyboardEvent, useCallback, ChangeEvent } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../App'
import { api, Message, MessageStatus } from '../services/api'
import { useSignalProtocol } from '../hooks/useSignalProtocol'
import { WSMessageTypes } from '../hooks/useWebSocket'
import { useSharedWebSocket } from '../contexts/WebSocketContext'
import ChatBubble from '../components/ChatBubble'

// Maximum file size: 50MB
const MAX_FILE_SIZE = 50 * 1024 * 1024

export default function ChatPage() {
  const { recipientId } = useParams<{ recipientId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [messages, setMessages] = useState<Message[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [recipientName] = useState(recipientId || '')
  const [isTyping, setIsTyping] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const cameraInputRef = useRef<HTMLInputElement>(null)

  const { encrypt, decryptAsync } = useSignalProtocol(recipientId || '', user?.id)
  const { lastMessage, isConnected } = useSharedWebSocket()

  // Load messages on mount
  useEffect(() => {
    if (recipientId) {
      loadMessages()
    }
  }, [recipientId])

  // Polling fallback when WebSocket is not connected
  useEffect(() => {
    if (isConnected || !recipientId) return // Don't poll if WebSocket is connected

    console.log('[Chat] WebSocket not connected, starting polling...')
    const pollInterval = setInterval(async () => {
      try {
        const data = await api.getMessages(recipientId)

        // Only update if there are new messages
        if (data.length > messages.length) {
          console.log('[Chat] Polling found new messages:', data.length - messages.length)

          // Decrypt new messages
          const newMessages = data.slice(messages.length)
          const decryptedNew = await Promise.all(
            newMessages.map(async (msg) => {
              if (msg.ciphertext) {
                try {
                  const decryptedText = await decryptAsync(msg.ciphertext)
                  return { ...msg, text: decryptedText }
                } catch {
                  return { ...msg, text: '[Unable to decrypt]' }
                }
              }
              return msg
            })
          )

          setMessages(prev => {
            // Avoid duplicates by checking message IDs
            const existingIds = new Set(prev.map(m => m.id))
            const uniqueNew = decryptedNew.filter(m => !existingIds.has(m.id))
            if (uniqueNew.length > 0) {
              // Show browser notification for new messages
              if ('Notification' in window && Notification.permission === 'granted') {
                uniqueNew.forEach(m => {
                  if (m.senderId === recipientId) {
                    new Notification('LYNKSTAR - 新訊息', {
                      body: `來自 ${recipientId.substring(0, 8)}... 的訊息`,
                      icon: '/favicon.ico',
                    })
                  }
                })
              }
              return [...prev, ...uniqueNew]
            }
            return prev
          })
        }
      } catch (error) {
        console.error('[Chat] Polling error:', error)
      }
    }, 3000) // Poll every 3 seconds

    return () => clearInterval(pollInterval)
  }, [isConnected, recipientId, messages.length, decryptAsync])

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Handle incoming WebSocket messages for real-time updates
  useEffect(() => {
    if (!lastMessage) return

    const handleIncomingMessage = async () => {
      // Handle new message from WebSocket
      if (lastMessage.type === WSMessageTypes.MESSAGE || lastMessage.type === 'new_message') {
        const payload = lastMessage.payload as {
          id?: string
          sender_id?: string
          senderId?: string
          ciphertext?: string
          text?: string
          timestamp?: string
          recipient_id?: string
          recipientId?: string
        }

        // Check if this message is for the current chat
        const msgSenderId = payload.sender_id || payload.senderId
        const msgRecipientId = payload.recipient_id || payload.recipientId

        // Only process if message is from current recipient or to current recipient
        if (msgSenderId !== recipientId && msgRecipientId !== recipientId) {
          // Message is for a different chat - show notification
          if (msgSenderId && msgSenderId !== user?.id) {
            showNotification(msgSenderId, payload.ciphertext || payload.text || '新訊息')
          }
          return
        }

        // Check if message already exists (avoid duplicates)
        const messageId = payload.id || `ws-${Date.now()}`
        setMessages(prev => {
          if (prev.some(m => m.id === messageId)) {
            return prev
          }

          // Decrypt the message
          const ciphertext = payload.ciphertext || payload.text || ''

          // Create new message object
          const newMsg: Message = {
            id: messageId,
            senderId: msgSenderId || '',
            text: '[解密中...]',
            timestamp: payload.timestamp || new Date().toISOString(),
            status: 'delivered' as MessageStatus,
            ciphertext,
          }

          // Add message and decrypt async
          decryptAsync(ciphertext).then(decryptedText => {
            setMessages(current =>
              current.map(m =>
                m.id === messageId ? { ...m, text: decryptedText } : m
              )
            )
          }).catch(() => {
            setMessages(current =>
              current.map(m =>
                m.id === messageId ? { ...m, text: '[無法解密]' } : m
              )
            )
          })

          return [...prev, newMsg]
        })
      }

      // Handle typing indicator
      if (lastMessage.type === WSMessageTypes.TYPING) {
        const payload = lastMessage.payload as { sender_id?: string }
        if (payload.sender_id === recipientId) {
          setIsTyping(true)
          // Clear typing indicator after 3 seconds
          setTimeout(() => setIsTyping(false), 3000)
        }
      }

      // Handle read receipts
      if (lastMessage.type === WSMessageTypes.READ) {
        const payload = lastMessage.payload as { message_ids?: string[] }
        if (payload.message_ids) {
          setMessages(prev =>
            prev.map(m =>
              payload.message_ids!.includes(m.id)
                ? { ...m, status: 'read' as MessageStatus }
                : m
            )
          )
        }
      }
    }

    handleIncomingMessage()
  }, [lastMessage, recipientId, user?.id, decryptAsync])

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  // Show browser notification
  const showNotification = useCallback((senderId: string, _text?: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification('LYNKSTAR - 新訊息', {
        body: `來自 ${senderId.substring(0, 8)}... 的訊息`,
        icon: '/favicon.ico',
        tag: `msg-${senderId}`,
      })

      notification.onclick = () => {
        window.focus()
        navigate(`/chat/${senderId}`)
        notification.close()
      }

      // Auto close after 5 seconds
      setTimeout(() => notification.close(), 5000)
    }
  }, [navigate])

  const loadMessages = async () => {
    try {
      setIsLoading(true)
      const data = await api.getMessages(recipientId!)

      // Try to find UUIDs from messages
      // My UUID: senderId that's different from the recipient
      // Their UUID: senderId that matches the recipient (if recipientId was username, we get UUID here)
      const myUUID = localStorage.getItem('lynkstar_my_uuid')
      let foundMyUUID: string | undefined
      let foundTheirUUID: string | undefined

      for (const msg of data) {
        if (!msg.senderId) continue

        // Check if this senderId is the recipient (their message)
        // If recipientId is a username, msg.senderId will be the UUID
        if (msg.senderId !== myUUID && !foundTheirUUID) {
          // This is likely the recipient's UUID
          foundTheirUUID = msg.senderId
          console.log('[Chat] Found recipient UUID from messages:', foundTheirUUID)
        }

        // Check if this is our message (senderId is our UUID)
        if (!foundMyUUID && msg.senderId !== foundTheirUUID) {
          foundMyUUID = msg.senderId
        }
      }

      // Store UUIDs for E2EE key derivation
      if (foundMyUUID) {
        console.log('[Chat] Found my UUID from messages:', foundMyUUID)
        localStorage.setItem('lynkstar_my_uuid', foundMyUUID)
      }

      // Store recipient UUID for session key derivation
      if (foundTheirUUID && foundTheirUUID !== recipientId) {
        console.log('[Chat] Storing recipient UUID mapping:', recipientId, '->', foundTheirUUID)
        // Store mapping from username/recipientId to UUID
        localStorage.setItem(`lynkstar_uuid_${recipientId}`, foundTheirUUID)
      }

      // Decrypt/decode messages
      const decryptedMessages = await Promise.all(
        data.map(async (msg) => {
          // Check if text field contains PLAIN: marker (MVP plaintext)
          if (msg.text && msg.text.startsWith('PLAIN:')) {
            try {
              const decryptedText = await decryptAsync(msg.text)
              return { ...msg, text: decryptedText }
            } catch {
              return msg
            }
          }
          // Check ciphertext field for encrypted or plaintext messages
          if (msg.ciphertext) {
            try {
              const decryptedText = await decryptAsync(msg.ciphertext)
              return { ...msg, text: decryptedText }
            } catch {
              return { ...msg, text: '[Unable to decrypt]' }
            }
          }
          return msg
        })
      )
      setMessages(decryptedMessages)
    } catch (error) {
      console.error('Failed to load messages:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSendMessage = async () => {
    const text = newMessage.trim()
    if (!text || !recipientId) return

    // Create optimistic message
    const optimisticMessage: Message = {
      id: `temp-${Date.now()}`,
      senderId: user?.id || 'me',
      text,
      timestamp: new Date().toISOString(),
      status: 'sending' as MessageStatus,
    }

    setMessages(prev => [...prev, optimisticMessage])
    setNewMessage('')
    inputRef.current?.focus()

    try {
      // Encrypt and send via REST API
      const ciphertext = await encrypt(text)
      const sentMessage = await api.sendMessage(recipientId, ciphertext)

      // Extract and store our UUID from the sent message response
      // This is crucial for symmetric key derivation
      if (sentMessage.senderId && sentMessage.senderId !== recipientId) {
        const currentStoredUUID = localStorage.getItem('lynkstar_my_uuid')
        if (!currentStoredUUID) {
          console.log('[Chat] Storing my UUID from sent message:', sentMessage.senderId)
          localStorage.setItem('lynkstar_my_uuid', sentMessage.senderId)
        } else if (currentStoredUUID !== sentMessage.senderId) {
          // UUID mismatch - this shouldn't happen normally
          console.warn('[Chat] UUID mismatch! stored:', currentStoredUUID, 'got:', sentMessage.senderId)
        }
      }

      // Update message with server response
      setMessages(prev =>
        prev.map(msg =>
          msg.id === optimisticMessage.id
            ? { ...sentMessage, text, status: 'sent' as MessageStatus }
            : msg
        )
      )
    } catch (error) {
      console.error('Failed to send message:', error)
      // Update message status to failed
      setMessages(prev =>
        prev.map(msg =>
          msg.id === optimisticMessage.id
            ? { ...msg, status: 'failed' as MessageStatus }
            : msg
        )
      )
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  // Handle file selection for upload
  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0 || !recipientId) return

    const file = files[0]

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setUploadError(`文件大小超過限制 (最大 ${MAX_FILE_SIZE / 1024 / 1024}MB)`)
      return
    }

    // Validate file type
    const isImage = file.type.startsWith('image/')
    const isVideo = file.type.startsWith('video/')
    if (!isImage && !isVideo) {
      setUploadError('只支持圖片和影片文件')
      return
    }

    setShowAttachmentMenu(false)
    setUploadError(null)
    setUploadProgress(0)

    // Create optimistic message with preview
    const optimisticMessage: Message = {
      id: `temp-${Date.now()}`,
      senderId: user?.id || 'me',
      text: isImage ? '[圖片]' : '[影片]',
      timestamp: new Date().toISOString(),
      status: 'sending' as MessageStatus,
    }

    setMessages(prev => [...prev, optimisticMessage])

    try {
      // Upload file
      const result = await api.uploadFile(file, recipientId)
      setUploadProgress(50)

      // Create media message content
      const mediaContent = JSON.stringify({
        type: isImage ? 'image' : 'video',
        url: result.url,
        id: result.id,
        name: file.name,
        size: file.size,
      })

      // Encrypt and send
      const ciphertext = await encrypt(`[MEDIA]${mediaContent}`)
      const sentMessage = await api.sendMessage(recipientId, ciphertext)

      setUploadProgress(100)

      // Update message
      setMessages(prev =>
        prev.map(msg =>
          msg.id === optimisticMessage.id
            ? {
                ...sentMessage,
                text: `[MEDIA]${mediaContent}`,
                status: 'sent' as MessageStatus,
              }
            : msg
        )
      )
    } catch (error) {
      console.error('Failed to upload file:', error)
      setUploadError('上傳失敗，請重試')
      setMessages(prev =>
        prev.map(msg =>
          msg.id === optimisticMessage.id
            ? { ...msg, status: 'failed' as MessageStatus }
            : msg
        )
      )
    } finally {
      setUploadProgress(null)
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = ''
      if (cameraInputRef.current) cameraInputRef.current.value = ''
    }
  }

  // Open gallery picker
  const openGallery = () => {
    fileInputRef.current?.click()
  }

  // Open camera
  const openCamera = () => {
    cameraInputRef.current?.click()
  }

  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp)
    // Use toLocaleTimeString for proper timezone handling
    return date.toLocaleTimeString('zh-TW', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-dark-bg">
      {/* App Bar */}
      <header className="app-bar sticky top-0 z-10">
        {/* Back Button */}
        <button
          onClick={() => navigate('/chats')}
          className="p-1 hover:bg-white/10 rounded-full transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Avatar */}
        <div className="avatar avatar-sm bg-primary-light text-primary">
          {recipientName.charAt(0).toUpperCase()}
        </div>

        {/* Title & Status */}
        <div className="flex-1 min-w-0">
          <h1 className="font-semibold truncate">{recipientName}</h1>
          <div className="flex items-center gap-1">
            {isTyping ? (
              <span className="text-xs text-primary animate-pulse">正在輸入...</span>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 text-secure-green" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
                </svg>
                <span className="text-xs text-secure-green">端對端加密</span>
                {isConnected && (
                  <span className="text-xs text-green-500 ml-1">(即時)</span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Video Call */}
        <button
          onClick={() => navigate(`/call/${recipientId}?type=video`)}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        </button>

        {/* Voice Call */}
        <button
          onClick={() => navigate(`/call/${recipientId}?type=audio`)}
          className="p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        </button>

        {/* Menu */}
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {showMenu && (
            <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-surface rounded-xl shadow-lg z-50 overflow-hidden animate-fade-in">
              <button className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
                Verify Identity
              </button>
              <button className="w-full px-4 py-3 text-left text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Clear Chat
              </button>
              <button className="w-full px-4 py-3 text-left text-error hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                Block User
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="spinner border-primary border-t-primary-light" />
          </div>
        ) : messages.length === 0 ? (
          /* Empty State */
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-20 h-20 bg-secure-green/10 rounded-full flex items-center justify-center mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-secure-green" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <p className="text-secure-green text-sm font-medium mb-2">
              Messages are end-to-end encrypted
            </p>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Only you and {recipientName} can read these messages
            </p>
          </div>
        ) : (
          /* Messages List */
          <div className="space-y-2">
            {messages.map((message) => {
              // Determine if message is sent by me or received from recipient
              // - If senderId === recipientId → received from them (show on LEFT)
              // - Otherwise → sent by me (show on RIGHT)
              const isReceived = message.senderId === recipientId
              const isSent = !isReceived
              return (
                <ChatBubble
                  key={message.id}
                  message={message}
                  isSent={isSent}
                  formatTime={formatMessageTime}
                />
              )
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </main>

      {/* Input Area */}
      <div className="bg-white dark:bg-dark-surface border-t border-gray-100 dark:border-gray-800 px-4 py-3 sticky bottom-0">
        <div className="flex items-end gap-2">
          {/* Emoji Button */}
          <button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>

          {/* Attachment Button */}
          <div className="relative">
            <button
              onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
            </button>

            {/* Hidden file inputs */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={handleFileSelect}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*,video/*"
              capture="environment"
              className="hidden"
              onChange={handleFileSelect}
            />

            {/* Attachment Menu */}
            {showAttachmentMenu && (
              <div className="absolute bottom-full left-0 mb-2 bg-white dark:bg-dark-surface rounded-xl shadow-lg p-4 animate-fade-in">
                <div className="flex gap-4">
                  <button onClick={openGallery} className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">相簿</span>
                  </button>
                  <button onClick={openCamera} className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 bg-pink-500 rounded-full flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">相機</span>
                  </button>
                  <button onClick={openGallery} className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">影片</span>
                  </button>
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {uploadProgress !== null && (
              <div className="absolute bottom-full left-0 mb-2 bg-white dark:bg-dark-surface rounded-xl shadow-lg p-3 animate-fade-in">
                <div className="flex items-center gap-2">
                  <div className="spinner border-primary border-t-primary-light w-4 h-4" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    上傳中... {uploadProgress}%
                  </span>
                </div>
              </div>
            )}

            {/* Upload Error */}
            {uploadError && (
              <div className="absolute bottom-full left-0 mb-2 bg-red-50 dark:bg-red-900/20 rounded-xl shadow-lg p-3 animate-fade-in">
                <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm">{uploadError}</span>
                  <button onClick={() => setUploadError(null)} className="ml-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Text Input */}
          <div className="flex-1">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              rows={1}
              className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-3xl resize-none
                         focus:outline-none focus:ring-2 focus:ring-primary/50
                         dark:text-white placeholder:text-gray-400"
              style={{ maxHeight: '120px' }}
            />
          </div>

          {/* Send Button */}
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            className="p-3 bg-primary text-white rounded-full
                       hover:bg-primary-dark transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>

      {/* Click outside to close menus */}
      {(showMenu || showAttachmentMenu) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowMenu(false)
            setShowAttachmentMenu(false)
          }}
        />
      )}
    </div>
  )
}
